import React from "react";

interface SkeletonLoadingProps {
    type?: 'student-card' | 'diary-card' | 'default';
    count?: number;
}

const SkeletonLoading: React.FC<SkeletonLoadingProps> = ({ 
    type = 'default', 
    count = 1 
}) => {
    const renderStudentCardSkeleton = () => (
        <div className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg animate-pulse">
            <div className="flex items-center space-x-3 cursor-pointer flex-1">
                {/* 프로필 이미지 스켈레톤 */}
                <div className="relative w-14 h-14">
                    <div className="w-full h-full bg-gray-200 rounded-full"></div>
                    {/* 국기 스켈레톤 */}
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 text-lg">
                        <div className="w-4 h-4 bg-gray-200 rounded"></div>
                    </div>
                </div>
                
                {/* 텍스트 정보 스켈레톤 */}
                <div className="flex-1">
                    {/* 생년월일, 국가 정보 */}
                    <div className="h-3 bg-gray-200 rounded w-24 mb-1"></div>
                    {/* 이름, 닉네임 */}
                    <div className="h-4 bg-gray-200 rounded w-32 mb-1"></div>
                    {/* 마지막 업데이트 시간 */}
                    <div className="h-3 bg-gray-200 rounded w-20"></div>
                </div>
            </div>
            
            {/* 업데이트 표시 스켈레톤 */}
            <div className="w-3 h-3 bg-gray-200 rounded-full ml-2"></div>
        </div>
    );

    const renderDiaryCardSkeleton = () => (
        <div className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow animate-pulse">
            {/* 다이어리 헤더 */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                    <div className="space-y-1">
                        <div className="h-3 bg-gray-200 rounded w-20"></div>
                        <div className="h-2 bg-gray-200 rounded w-16"></div>
                    </div>
                </div>
                <div className="flex items-center space-x-2">
                    <div className="h-6 bg-gray-200 rounded w-12"></div>
                    <div className="h-6 bg-gray-200 rounded w-12"></div>
                </div>
            </div>

            {/* 다이어리 제목 */}
            <div className="mb-1">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>

            {/* 다이어리 내용 */}
            <div className="mb-2">
                <div className="space-y-1">
                    <div className="h-3 bg-gray-200 rounded w-full"></div>
                    <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                    <div className="h-3 bg-gray-200 rounded w-4/5"></div>
                </div>
            </div>

            {/* 미디어 영역 (이미지/비디오) */}
            <div className="mb-3">
                <div className="h-48 bg-gray-200 rounded-lg"></div>
            </div>

            {/* 액션 버튼들 */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-1">
                        <div className="w-4 h-4 bg-gray-200 rounded"></div>
                        <div className="h-3 bg-gray-200 rounded w-8"></div>
                    </div>
                    <div className="flex items-center space-x-1">
                        <div className="w-4 h-4 bg-gray-200 rounded"></div>
                        <div className="h-3 bg-gray-200 rounded w-8"></div>
                    </div>
                </div>
                <div className="flex items-center space-x-2">
                    <div className="h-6 bg-gray-200 rounded w-12"></div>
                    <div className="h-6 bg-gray-200 rounded w-12"></div>
                </div>
            </div>
        </div>
    );

    const renderDefaultSkeleton = () => (
        <div className="animate-pulse">
            <div className="space-y-4">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
        </div>
    );

    const renderSkeleton = () => {
        switch (type) {
            case 'student-card':
                return renderStudentCardSkeleton();
            case 'diary-card':
                return renderDiaryCardSkeleton();
            default:
                return renderDefaultSkeleton();
        }
    };

    return (
        <div className="space-y-4">
            {Array.from({ length: count }, (_, index) => (
                <div key={index}>
                    {renderSkeleton()}
                </div>
            ))}
        </div>
    );
};

export default SkeletonLoading;
